import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle, Building2, Car, Check, Lock, ParkingSquare, Pencil, RefreshCw,
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import Plano from './estacionamiento/Plano';
import { GlifoTipo } from './estacionamiento/Emblemas';
import { ESTILOS } from './estacionamiento/estilos';
import { TIPOS, puestoUsable, puestosUsables } from './estacionamiento/estacionamientoLogic';

const ORDEN_TIPOS = ['libre', 'privado_sura', 'gerencia', 'privado'];

function Esqueleto() {
  return (
    <div className="est-piso" aria-hidden="true">
      <div className="est-sk-fila" style={{ justifyContent: 'center' }}>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
          <div key={i} className="est-sk" style={{ flex: i === 2 ? 0.5 : 1, height: 104, opacity: i === 2 || i === 5 ? 0 : 1 }} />
        ))}
      </div>
      <div className="est-sk-fila" style={{ paddingTop: 40, paddingBottom: 28 }}>
        {Array.from({ length: 16 }, (_, i) => (
          <div key={i} className="est-sk" style={{ height: 104 }} />
        ))}
      </div>
    </div>
  );
}

function Detalle({ puesto, esGerencia }) {
  if (!puesto) {
    return (
      <div className="est-card est-detalle" aria-live="polite">
        <div className="est-ficha est-vacia">
          <ParkingSquare size={32} aria-hidden="true" />
        </div>
        <div>
          <div className="est-eyebrow">Detalle del puesto</div>
          <p className="est-vacio-t" style={{ marginTop: 8 }}>
            Pasa el cursor, toca o enfoca un puesto del plano para ver su número, su tipo y quién puede usarlo.
          </p>
        </div>
      </div>
    );
  }

  const tipo = TIPOS[puesto.tipo] || TIPOS.privado;
  const usable = puestoUsable(puesto.tipo, esGerencia);
  const soloGerencia = puesto.tipo === 'gerencia' && !esGerencia;
  let estado;
  if (usable) {
    estado = <span className="est-insig est-ok"><Check size={15} aria-hidden="true" />Disponible para ti</span>;
  } else if (soloGerencia) {
    estado = <span className="est-insig est-no"><Lock size={14} aria-hidden="true" />Solo gerencia</span>;
  } else {
    estado = <span className="est-insig est-no"><Lock size={14} aria-hidden="true" />Puesto privado</span>;
  }

  return (
    <div className="est-card est-detalle" aria-live="polite">
      <div className={`est-ficha est-tipo-${puesto.tipo}`}>
        <GlifoTipo tipo={puesto.tipo} escala={52} />
        {puesto.tipo === 'privado' ? <span className="est-tag">Privado</span> : null}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="est-eyebrow">Puesto</div>
        <div className="est-det-num">{puesto.numero}</div>
        <div className="est-det-fila">
          <span className={`est-insig${puesto.tipo === 'libre' ? ' est-ins-lila' : ''}`}>{tipo.etiqueta}</span>
          {estado}
        </div>
        <p className="est-det-desc">{tipo.descripcion}</p>
      </div>
    </div>
  );
}

export default function EstacionamientoView() {
  const [estado, setEstado] = useState('cargando');
  const [mensajeError, setMensajeError] = useState('');
  const [puestos, setPuestos] = useState([]);
  const [esGerencia, setEsGerencia] = useState(false);
  const [puedeEditar, setPuedeEditar] = useState(false);

  const [filtro, setFiltro] = useState(null);
  const [seleccion, setSeleccion] = useState(null);
  const [hover, setHover] = useState(null);
  const [foco, setFoco] = useState(null);

  const [modoEdicion, setModoEdicion] = useState(false);
  const [selectorPara, setSelectorPara] = useState(null);
  const [guardando, setGuardando] = useState(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  const cargar = useCallback(async () => {
    setEstado('cargando');
    try {
      const res = await axiosInstance.get('/api/estacionamiento');
      if (!montado.current) return;
      const datos = res.data || {};
      setPuestos(Array.isArray(datos.puestos) ? datos.puestos : []);
      setEsGerencia(Boolean(datos.es_gerencia));
      setPuedeEditar(Boolean(datos.puede_editar));
      setEstado('ok');
    } catch (err) {
      if (!montado.current) return;
      const texto = err && err.response && err.response.data && err.response.data.error;
      setMensajeError(texto || 'No se pudo cargar el plano del estacionamiento.');
      setEstado('error');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const conteos = useMemo(() => {
    const c = { libre: 0, privado_sura: 0, gerencia: 0, privado: 0 };
    puestos.forEach((p) => { if (c[p.tipo] !== undefined) c[p.tipo] += 1; });
    return c;
  }, [puestos]);

  const disponibles = useMemo(() => puestosUsables(puestos, esGerencia).length, [puestos, esGerencia]);

  const alPulsarPuesto = useCallback((numero) => {
    if (modoEdicion) {
      setSeleccion(numero);
      setSelectorPara((prev) => (prev === numero ? null : numero));
    } else {
      setSeleccion((prev) => (prev === numero ? null : numero));
    }
  }, [modoEdicion]);

  const cerrarSelector = useCallback(() => setSelectorPara(null), []);

  const alternarEdicion = () => {
    setModoEdicion((v) => !v);
    setSelectorPara(null);
  };

  const cambiarTipo = useCallback(async (numero, tipo) => {
    const actual = puestos.find((p) => p.numero === numero);
    if (!actual || actual.tipo === tipo) {
      setSelectorPara(null);
      return;
    }
    setGuardando(numero);
    try {
      const res = await axiosInstance.put(`/api/estacionamiento/${numero}`, { tipo });
      if (!montado.current) return;
      const nuevo = res.data || { numero, tipo };
      setPuestos((prev) => prev.map((p) => (p.numero === nuevo.numero ? { ...p, ...nuevo } : p)));
      setSelectorPara(null);
      const etiqueta = TIPOS[nuevo.tipo] ? TIPOS[nuevo.tipo].etiqueta : nuevo.tipo;
      toast.success(`Puesto ${nuevo.numero}: ${etiqueta}`);
    } catch (err) {
      const texto = err && err.response && err.response.data && err.response.data.error;
      toast.error(texto || 'No se pudo guardar el cambio. Intenta de nuevo.');
    } finally {
      if (montado.current) setGuardando(null);
    }
  }, [puestos]);

  const numeroActivo = hover !== null ? hover : (foco !== null ? foco : seleccion);
  const puestoActivo = puestos.find((p) => p.numero === numeroActivo) || null;

  return (
    <div className="est-root">
      <style>{ESTILOS}</style>

      <header className="est-cab">
        <div className="est-cab-izq">
          <div>
            <h1 className="est-h1">Estacionamiento (CA1)</h1>
            <p className="est-sub">Piso 3 · Distribución de los 26 puestos</p>
          </div>
        </div>
        {estado === 'ok' ? (
          <div className="est-cab-der">
            <span className="est-stat">
              <Car size={18} color="#003366" aria-hidden="true" />
              Puedes usar <b>{disponibles}</b> de {puestos.length} puestos
            </span>
            {puedeEditar ? (
              <button
                type="button"
                className={`est-btn${modoEdicion ? ' est-btn-claro' : ''}`}
                onClick={alternarEdicion}
                aria-pressed={modoEdicion}
              >
                {modoEdicion ? <Check size={17} aria-hidden="true" /> : <Pencil size={17} aria-hidden="true" />}
                {modoEdicion ? 'Terminar edición' : 'Editar plano'}
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {estado === 'ok' ? (
        <div className="est-leyenda" role="group" aria-label="Filtrar puestos por tipo">
          {ORDEN_TIPOS.map((t) => (
            <button
              key={t}
              type="button"
              className="est-chip"
              aria-pressed={filtro === t}
              onClick={() => setFiltro((prev) => (prev === t ? null : t))}
            >
              <span className={`est-mini est-tipo-${t}`}>
                <GlifoTipo tipo={t} escala={22} />
              </span>
              <span className="est-chip-txt">
                {TIPOS[t].etiqueta}
                {t === 'gerencia' && !esGerencia ? <span className="est-chip-sub">Solo gerencia</span> : null}
              </span>
              <span className="est-chip-n" aria-label={`${conteos[t]} puestos`}>{conteos[t]}</span>
            </button>
          ))}
          {filtro ? (
            <button type="button" className="est-limpiar" onClick={() => setFiltro(null)}>
              Quitar filtro
            </button>
          ) : null}
        </div>
      ) : null}

      <section className="est-card est-plano-card" aria-label="Plano del estacionamiento, piso 3">
        <div className="est-card-cab">
          <h2 className="est-card-tit">
            Plano del piso 3 <span>· 26 puestos</span>
          </h2>
          {modoEdicion ? (
            <span className="est-aviso">
              <Pencil size={15} aria-hidden="true" />
              Modo edición: elige un puesto para cambiar su tipo
            </span>
          ) : null}
        </div>

        {estado === 'cargando' ? (
          <div role="status" aria-live="polite">
            <span className="est-sr">Cargando el plano del estacionamiento…</span>
            <Esqueleto />
          </div>
        ) : null}

        {estado === 'error' ? (
          <div className="est-piso">
            <div className="est-error" role="alert">
              <AlertCircle size={38} color="#B42318" aria-hidden="true" />
              <h2>No pudimos cargar el plano</h2>
              <p>{mensajeError}</p>
              <button type="button" className="est-btn" onClick={cargar}>
                <RefreshCw size={16} aria-hidden="true" />
                Reintentar
              </button>
            </div>
          </div>
        ) : null}

        {estado === 'ok' ? (
          <Plano
            puestos={puestos}
            esGerencia={esGerencia}
            filtro={filtro}
            seleccionado={seleccion}
            activo={numeroActivo}
            modoEdicion={modoEdicion}
            selectorPara={selectorPara}
            guardando={guardando}
            onEnter={setHover}
            onLeave={() => setHover(null)}
            onFoco={setFoco}
            onDesenfoque={() => setFoco(null)}
            onPulsar={alPulsarPuesto}
            onElegirTipo={cambiarTipo}
            onCerrarSelector={cerrarSelector}
          />
        ) : null}
      </section>

      {estado === 'ok' ? (
        <div className="est-inferior">
          <Detalle puesto={puestoActivo} esGerencia={esGerencia} />
          <aside className="est-card est-terraza" aria-label="Terraza">
            <div className="est-terraza-ico">
              <Building2 size={28} color="#5B4F8E" aria-hidden="true" />
            </div>
            <p>
              <strong>Disponible todo el piso de la terraza</strong> para estacionarse.
            </p>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
